import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, subDays, setHours, setMinutes, setSeconds } from 'date-fns';
import * as Yup from 'yup';
import { GPP, FRENTISTA } from '~/constants/perfil';
import { includes, isEmpty, isNull } from 'lodash';

import api from '~/services/api';
import { t, parseDateString } from '~/helpers';
import { Report } from '~/components';
import {
  DateTime,
  Autocomplete,
  RadioGroup,
  Select
} from '~/components/Form';

export default function SaldoCombustivelNosPostosData() {
  const [open, setOpen] = useState(true);
  const [opcaoPosto, setOpcaoPosto] = useState([]);
  const [post, setPost] = useState({});
  const [product, setProduct] = useState({});
  const [tank, setTank] = useState({ tafi_nome: '', tafi_codigo: '' });

  const reportRef = useRef(null)

  const { profile } = useSelector((state) => state.user);

  const umPosto = useMemo(() => includes([GPP, FRENTISTA], profile.glousuariotipo), [profile.glousuariotipo]);

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(subDays(new Date(), 7), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const handleCloseReport = useCallback(() => {
    setOpen(false);
  }, []);

  const handleValidate = useCallback(async (data) => {
    try {
      const schema = Yup.object().shape({
        datahorainicial: Yup.date()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .transform((_, value) => parseDateString(value))
          .required(t('s.geral.validacao.campoobrigatorio')),
        datahorafinal: Yup.date()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .transform((_, value) => parseDateString(value))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            datahorafinal,
            `A data não pode ser superior a ${datahorafinal}`
          )
          .min(
            Yup.ref('datahorainicial'),
            'A data não pode ser inferior a Data inicial'
          ),
        produto: Yup.string().nullable().required(t('s.geral.validacao.campoobrigatorio')),
        posto: Yup.string().nullable().required(t('s.geral.validacao.campoobrigatorio')),
      });

      await schema.validate(data, { abortEarly: false });
    } catch (err) {
      const form = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          form[error.path] = error.message.replace(`${error.path} `, '');
        });

        throw { form };
      }
    }
  }, []);

  const handleChangeTank = useCallback((value) => {
    if (isNull(value.tafi_nome)) setTank({ tafi_nome: '', tafi_codigo: '' });
    else{
      setTank(value);
    } 
  }, []);

  const onSerializeData = useCallback((data) => ({
    ...data,
    produtostr: product.prod_descricao,
    postostr: post.poco_descricao,
    tanquestr: tank.tafi_nome ?? '',
    municipio: post.muni_nome
  }), [product, post, tank]);

  return (
    <Report
      ref={reportRef}
      open={open}
      onClose={handleCloseReport}
      serialize={onSerializeData}
      validate={handleValidate}
      title="Saldo de Combustível no Posto no Período - Data"
      relatorio="SALDOCOMBUSTIVELPOSTOPERIODOTANQUE"
      initials={{
        datahorainicial,
        datahorafinal,
        formato: 'pdf',
        posto: profile.glousuariopostocodigo,
      }}
    >
      <DateTime time label="Data Inicial" name="datahorainicial" />

      <DateTime time label="Data Final" name="datahorafinal" />

      <Autocomplete
        name="produto"
        label="prod_display"
        title="Produto"
        url="produtos"
        unique="prod_codigo"
        getValue={(value) =>{
          setProduct(value);
          reportRef.current.clearField('tanque');
          setTank({ tafi_nome: '', tafi_codigo: '' });
        } }
      />

      <Autocomplete
        name="posto"
        label="poco_desc_abrev"
        title="Posto"
        url="postosporusuario"
        unique="poco_codigo"
        disabled={umPosto}
        getValue={(value) => {
          setPost(value);
          reportRef.current.clearField('tanque');
          setTank({ tafi_nome: '', tafi_codigo: '' });
        }}
      />

      <Autocomplete
       includeInputInList
       disabledItemsFocusable
       disableClearable={!tank.tafi_codigo}
        name="tanque"
        label="tafi_nome"
        title="Tanques"
        url="tanquesfisicos"
        unique="tafi_codigo"
        params={{
          wherefield: ['prod_codigo', 'poco_codigo'],
          valuewherefield: [product.prod_codigo, post.poco_codigo],
          typewherefield: ['EQUALS', 'EQUALS']
        }}
        disabled={isEmpty(post) || isEmpty(product)}
        getValue={handleChangeTank}
        inputValue={tank.tafi_nome}
      />

      <RadioGroup
        row
        name="tipo"
        label="Formato"
        options={[
          { label: 'PDF', value: 'pdf' },
          { label: 'Excel', value: 'xls' },
        ]}
        defaultValue="pdf"
      />
    </Report>
  )
}
